import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Container,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { auth } from '../config/firebase';
import { confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import api from '../utils/api';

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: 'error' | 'warning' | 'info' | 'success';
}

const AccountSetup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token and email from URL params
  const oobCode = searchParams.get('oobCode');
  const email = searchParams.get('email') || '';

  const [formData, setFormData] = useState({
    email: email,
    fullName: '',
    password: '',
    confirmPassword: '',
  });

  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    color: 'error',
  });

  useEffect(() => {
    // Validate invitation link
    if (!oobCode) {
      setError('Invalid invitation link. Please contact your administrator for a new invitation.');
    }
  }, [oobCode]);

  // Password strength checker
  useEffect(() => {
    const checkPasswordStrength = (password: string): PasswordStrength => {
      let score = 0;
      const feedback: string[] = [];

      if (password.length === 0) {
        return { score: 0, feedback: [], color: 'error' };
      }

      if (password.length < 8) {
        feedback.push('Password should be at least 8 characters');
      } else {
        score += 1;
      }

      if (!/[A-Z]/.test(password)) {
        feedback.push('Include at least one uppercase letter');
      } else {
        score += 1;
      }

      if (!/[a-z]/.test(password)) {
        feedback.push('Include at least one lowercase letter');
      } else {
        score += 1;
      }

      if (!/\d/.test(password)) {
        feedback.push('Include at least one number');
      } else {
        score += 1;
      }

      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        feedback.push('Include at least one special character');
      } else {
        score += 1;
      }

      let color: 'error' | 'warning' | 'info' | 'success' = 'error';
      if (score >= 4) color = 'success';
      else if (score >= 3) color = 'info';
      else if (score >= 2) color = 'warning';

      return { score, feedback, color };
    };

    setPasswordStrength(checkPasswordStrength(formData.password));
  }, [formData.password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!oobCode) {
      setError('Invalid invitation link');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordStrength.score < 3) {
      setError('Please create a stronger password');
      return;
    }

    if (!formData.fullName.trim()) {
      setError('Please enter your full name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirm the password reset with the new password
      await confirmPasswordReset(auth, oobCode, formData.password);

      // Sign the user in with their new credentials
      await signInWithEmailAndPassword(auth, formData.email, formData.password);

      // Update user profile with full name
      try {
        await api.put('/users/me', { name: formData.fullName });
      } catch (profileError) {
        console.warn('Could not update user profile:', profileError);
      }

      setSuccess(true);
      
      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 2000);

    } catch (error: any) {
      console.error('Account setup error:', error);
      if (error.code === 'auth/expired-action-code') {
        setError('This invitation link has expired. Please contact your administrator for a new invitation.');
      } else if (error.code === 'auth/invalid-action-code') {
        setError('This invitation link is invalid. Please contact your administrator for a new invitation.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to set up your account. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    if (error) setError(null);
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          sx={{ py: 4 }}
        >
          <Box
            component="img"
            src="/StableTrack.png"
            alt="StableTrack"
            sx={{
              height: 120,
              width: 120,
              mb: 4,
              objectFit: 'contain'
            }}
          />
          <Card sx={{ p: 4, textAlign: 'center', maxWidth: '100%' }}>
            <CardContent>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="success.main">
                Welcome to StableTrack!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your account has been successfully set up. You'll be redirected to the dashboard in a moment.
              </Typography>
              <LinearProgress sx={{ width: '100%' }} />
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ py: 4 }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="/StableTrack.png"
          alt="StableTrack"
          sx={{
            height: 120,
            width: 120,
            mb: 4,
            objectFit: 'contain'
          }}
        />

        {/* Main Card */}
        <Card sx={{ p: 4, width: '100%', maxWidth: 500 }}>
          <CardContent>
            <Typography variant="h4" align="center" gutterBottom sx={{ color: '#C9772B', fontFamily: '"Crimson Text", serif' }}>
              Complete Your Account Setup
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              You've been invited to join StableTrack. Please set your password to complete your account setup.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                sx={{ mb: 3 }}
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                required
                sx={{ mb: 3 }}
                placeholder="Enter your full name"
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                required
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Strength Indicator */}
              {formData.password && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(passwordStrength.score / 5) * 100}
                    color={passwordStrength.color}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color={`${passwordStrength.color}.main`}>
                    Password Strength: {passwordStrength.score < 2 ? 'Weak' : passwordStrength.score < 4 ? 'Medium' : 'Strong'}
                  </Typography>
                  {passwordStrength.feedback.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {passwordStrength.feedback.map((feedback, index) => (
                        <Typography key={index} variant="caption" display="block" color="text.secondary">
                          • {feedback}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                required
                sx={{ mb: 4 }}
                error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword !== '' && formData.password !== formData.confirmPassword
                    ? 'Passwords do not match'
                    : ''
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !oobCode}
                sx={{
                  py: 1.5,
                  backgroundColor: '#C9772B',
                  '&:hover': {
                    backgroundColor: '#6B3410',
                  },
                  fontFamily: '"Crimson Text", serif',
                  fontSize: '1.1rem',
                }}
              >
                {loading ? 'Setting up your account...' : 'Complete Setup'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 4, fontFamily: '"Crimson Text", serif' }}
        >
          © 2025 StableTrack. Professional veterinary practice management.
        </Typography>
      </Box>
    </Container>
  );
};

export default AccountSetup;
